/**
 * API Key Service
 * Frontend service for managing API keys
 */

import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

const isLocalhost = () => {
  return typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost')
  );
};

const getApiKeyFunctionUrl = (functionName: string): string => {
  const envAny = (import.meta as any).env || {};
  const directUrl: string | undefined = envAny.VITE_FUNCTIONS_BASE;
  
  // If explicit base URL is set in env, use it
  if (directUrl) {
    return `${directUrl}/${functionName}`;
  }
  
  // Auto-detect based on hostname
  if (isLocalhost()) {
    return `http://localhost:5001/lemur-476302/us-central1/${functionName}`;
  }
  
  // Production URL
  return `https://us-central1-lemur-476302.cloudfunctions.net/${functionName}`;
};

/**
 * Get Firebase Auth token for API requests
 */
async function getAuthToken(forceRefresh: boolean = false): Promise<string | null> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    console.warn('[API Keys] No authenticated user found');
    return null;
  }
  
  // Force refresh token to ensure we have the latest user's token
  const token = await user.getIdToken(forceRefresh);
  
  // Log for debugging (don't log the actual token)
  console.log('[API Keys] Using auth token for user:', {
    uid: user.uid,
    email: user.email,
    tokenLength: token?.length || 0,
  });
  
  return token;
}

export interface ApiKey {
  id: string;
  label: string;
  active: boolean;
  revoked: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  scopes: string[];
  metadata: Record<string, any>;
}

export interface CreateApiKeyRequest {
  label?: string;
  scopes?: string[];
  metadata?: Record<string, any>;
}

export interface CreateApiKeyResponse {
  apiKey: string; // Only shown once!
  id: string;
  label: string;
  createdAt: string;
  scopes: string[];
}

/**
 * Create a new API key
 */
export async function createApiKey(request: CreateApiKeyRequest = {}): Promise<CreateApiKeyResponse> {
  // Force refresh token to ensure we have the current user's token
  const token = await getAuthToken(true);
  if (!token) {
    throw new Error('User must be authenticated');
  }

  const url = getApiKeyFunctionUrl('createApiKey');
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      // Handle 404 - endpoint not deployed
      if (response.status === 404) {
        throw new Error('API key endpoints are not deployed yet. Please deploy Firebase Functions: firebase deploy --only functions:createApiKey,functions:listApiKeys,functions:revokeApiKey');
      }
      
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || error.message || 'Failed to create API key');
    }

    return await response.json();
  } catch (error: any) {
    // Handle network errors
    if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
      const isLocal = isLocalhost();
      const deployHint = isLocal 
        ? 'Make sure Firebase emulators are running: firebase emulators:start --only functions'
        : 'Please deploy Firebase Functions: firebase deploy --only functions:createApiKey,functions:listApiKeys,functions:revokeApiKey';
      
      throw new Error(`Cannot connect to API key service. ${deployHint}`);
    }
    throw error;
  }
}

/**
 * List all API keys for the current user
 * Queries Firestore directly - security rules ensure users can only see their own keys
 */
export async function listApiKeys(): Promise<ApiKey[]> {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const userId = user.uid;
  console.log('[API Keys Service] Querying keys for user:', userId);

  try {
    // Query Firestore directly - security rules will enforce access control
    const apiKeysRef = collection(db, 'api_keys');
    const q = query(apiKeysRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    console.log('[API Keys Service] Found keys:', snapshot.docs.length);

    const keys: ApiKey[] = snapshot.docs
      .map((docSnapshot) => {
        const data = docSnapshot.data();
        
        // Handle timestamp conversion
        let createdAt: string | null = null;
        if (data.createdAt) {
          if (data.createdAt.toDate) {
            // Firestore Timestamp
            createdAt = data.createdAt.toDate().toISOString();
          } else if (data.createdAt instanceof Date) {
            createdAt = data.createdAt.toISOString();
          } else if (typeof data.createdAt === 'string') {
            createdAt = data.createdAt;
          }
        }

        let lastUsedAt: string | null = null;
        if (data.lastUsedAt) {
          if (data.lastUsedAt.toDate) {
            lastUsedAt = data.lastUsedAt.toDate().toISOString();
          } else if (data.lastUsedAt instanceof Date) {
            lastUsedAt = data.lastUsedAt.toISOString();
          } else if (typeof data.lastUsedAt === 'string') {
            lastUsedAt = data.lastUsedAt;
          }
        }

        let expiresAt: string | null = null;
        if (data.expiresAt) {
          if (data.expiresAt.toDate) {
            expiresAt = data.expiresAt.toDate().toISOString();
          } else if (data.expiresAt instanceof Date) {
            expiresAt = data.expiresAt.toISOString();
          } else if (typeof data.expiresAt === 'string') {
            expiresAt = data.expiresAt;
          }
        }

        return {
          id: docSnapshot.id,
          label: data.label || 'Unnamed Key',
          active: data.active !== false,
          revoked: data.revoked === true,
          createdAt,
          lastUsedAt,
          expiresAt,
          scopes: data.scopes || [],
          metadata: data.metadata || {},
        };
      })
      // Sort by createdAt descending (newest first)
      .sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.localeCompare(a.createdAt);
      });

    console.log('[API Keys Service] Returning keys:', keys.length);
    return keys;
  } catch (error: any) {
    console.error('[API Keys Service] Error querying keys:', error);
    throw new Error(error.message || 'Failed to list API keys');
  }
}

/**
 * Revoke an API key
 * Updates Firestore directly - security rules ensure users can only revoke their own keys
 */
export async function revokeApiKey(keyId: string): Promise<void> {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User must be authenticated');
  }

  try {
    // Update the key document - security rules will verify ownership
    const keyRef = doc(db, 'api_keys', keyId);
    await updateDoc(keyRef, {
      active: false,
      revoked: true,
      revokedAt: Timestamp.now(),
    });
    
    console.log('[API Keys Service] Revoked key:', keyId);
  } catch (error: any) {
    console.error('[API Keys Service] Error revoking key:', error);
    throw new Error(error.message || 'Failed to revoke API key');
  }
}


